import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  MessageCircle,
  RefreshCcw,
  Send,
  Inbox,
  Loader2,
  Smartphone,
  Search,
  CheckCheck,
  AlertTriangle,
  Clock,
  Phone,
  Trash2,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { communicationsSmsAPI } from '../services/api';

const normalizeSearchText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('pt-PT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(value || '-');
  }
};

const stateLabel = (state) => {
  const raw = String(state || '').toLowerCase();
  if (raw.includes('notsentoffline')) return 'Não enviada';
  if (raw.includes('delivered')) return 'Entregue';
  if (raw.includes('failed') || raw.includes('error')) return 'Falhou';
  if (raw.includes('sent')) return 'Enviada';
  if (raw.includes('processed')) return 'Processada';
  if (raw.includes('queued')) return 'Em fila';
  if (raw.includes('scheduled')) return 'Agendada';
  if (raw.includes('pending')) return 'Pendente';
  if (raw.includes('received') || raw.includes('inbound')) return 'Recebida';
  return state || '-';
};

const stateBadgeClass = (state) => {
  const raw = String(state || '').toLowerCase();
  if (raw.includes('notsentoffline')) return 'bg-orange-900/40 text-orange-300 border-orange-800';
  if (raw.includes('delivered')) return 'bg-green-900/40 text-green-300 border-green-800';
  if (raw.includes('failed') || raw.includes('error')) return 'bg-red-900/40 text-red-300 border-red-800';
  if (raw.includes('sent')) return 'bg-blue-900/40 text-blue-300 border-blue-800';
  if (raw.includes('processed')) return 'bg-cyan-900/40 text-cyan-300 border-cyan-800';
  if (raw.includes('queued') || raw.includes('scheduled') || raw.includes('pending')) return 'bg-yellow-900/40 text-yellow-300 border-yellow-800';
  if (raw.includes('received') || raw.includes('inbound')) return 'bg-purple-900/40 text-purple-300 border-purple-800';
  return 'bg-slate-800 text-slate-300 border-slate-700';
};

const communicationTypeLabel = (item) => {
  const state = String(item?.state || '').toLowerCase();
  if (state.includes('notsentoffline')) return 'Não enviada';
  if (item?.direction === 'inbound') return 'Recebida';
  if (item?.direction === 'outbound') return 'Enviada';
  return item?.direction || '-';
};

const communicationTypeIconClass = (item) => {
  const state = String(item?.state || '').toLowerCase();
  if (state.includes('notsentoffline')) return 'text-orange-300';
  if (state.includes('failed') || state.includes('error')) return 'text-red-300';
  if (item?.direction === 'inbound') return 'text-purple-300';
  return 'text-green-300';
};

const parseRecipientPhones = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const customerPhone = (customer) =>
  String(customer?.phone || customer?.phone_number || customer?.mobile || customer?.mobile_phone || '').trim();

const customerDisplayName = (customer) =>
  String(customer?.name || customer?.full_name || customer?.customer_name || 'Cliente').trim();

const normalizePhoneKey = (value) =>
  String(value || '')
    .replace(/^00/, '+')
    .replace(/[^\d+]/g, '')
    .trim();

const phoneNationalDigits = (value) =>
  normalizePhoneKey(value).replace(/\D+/g, '').slice(-9);

const smsDateValue = (item) =>
  item?.received_at || item?.sent_at || item?.scheduled_for || item?.created_at || '';

const deviceLastSeenStatus = (lastSeen) => {
  if (!lastSeen) {
    return {
      label: 'Sem informação',
      stale: true,
      detail: 'O SMSGate não devolveu a última comunicação do telemóvel.',
    };
  }

  const parsed = new Date(lastSeen);
  if (Number.isNaN(parsed.getTime())) {
    return {
      label: 'Data inválida',
      stale: true,
      detail: String(lastSeen),
    };
  }

  const diffMs = Date.now() - parsed.getTime();
  const diffMinutes = Math.round(diffMs / 60000);

  if (diffMinutes < 0) {
    return {
      label: 'Relógio divergente',
      stale: false,
      detail: formatDateTime(lastSeen),
    };
  }

  if (diffMinutes <= 5) {
    return {
      label: 'Online agora',
      stale: false,
      detail: `Visto há ${diffMinutes} min`,
    };
  }

  if (diffMinutes < 60) {
    return {
      label: 'Possivelmente offline',
      stale: true,
      detail: `Sem contacto há ${diffMinutes} min`,
    };
  }

  const diffHours = Math.round(diffMinutes / 60);
  return {
    label: 'Offline/desatualizado',
    stale: true,
    detail: `Sem contacto há cerca de ${diffHours} h`,
  };
};

export default function CommunicationsSMSPage() {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingStatus, setSyncingStatus] = useState(false);
  const [sending, setSending] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [filterDirection, setFilterDirection] = useState('all');
  const [filterUnread, setFilterUnread] = useState(false);
  const [search, setSearch] = useState('');

  const [showSendModal, setShowSendModal] = useState(false);
  const [sendForm, setSendForm] = useState({
    to: '',
    message: '',
    schedule_at: '',
  });
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedConversationSms, setSelectedConversationSms] = useState(null);
  const [conversationReply, setConversationReply] = useState('');
  const [conversationSending, setConversationSending] = useState(false);
  const [conversationCustomers, setConversationCustomers] = useState([]);
  const [conversationCustomerLoading, setConversationCustomerLoading] = useState(false);
  const [phoneCustomerMap, setPhoneCustomerMap] = useState({});
  const conversationEndRef = useRef(null);
  const silentRefreshInFlightRef = useRef(false);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await communicationsSmsAPI.getStatus();
      setStatus(res.data || null);
    } catch (error) {
      console.error('Erro ao carregar estado SMSGate:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao carregar estado do SMSGate');
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (options = {}) => {
    const silent = Boolean(options?.silent);

    if (!silent) {
      setLoading(true);
    }

    try {
      const params = { limit: 200 };
      if (filterDirection !== 'all') params.direction = filterDirection;
      if (filterUnread) params.unread_only = true;

      const res = await communicationsSmsAPI.getAll(params);
      const data = Array.isArray(res.data) ? res.data : [];
      setMessages(data);
    } catch (error) {
      console.error('Erro ao carregar comunicações SMS:', error);
      if (!silent) {
        toast.error(error?.response?.data?.detail || 'Erro ao carregar comunicações SMS');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [filterDirection, filterUnread]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (silentRefreshInFlightRef.current) return;

      silentRefreshInFlightRef.current = true;
      loadMessages({ silent: true }).finally(() => {
        silentRefreshInFlightRef.current = false;
      });
    }, 10000);

    return () => window.clearInterval(timer);
  }, [loadMessages]);


  useEffect(() => {
    const visiblePhones = Array.from(new Set(
      messages
        .map((item) => item?.phone_number)
        .map(phoneNationalDigits)
        .filter(Boolean)
    ));

    const missingPhones = visiblePhones.filter((phoneKey) => !(phoneKey in phoneCustomerMap));
    if (!missingPhones.length) return;

    let cancelled = false;

    const loadMissingPhoneNames = async () => {
      const updates = {};

      for (const phoneKey of missingPhones.slice(0, 25)) {
        const customers = await findCustomersByPhone(phoneKey, 20);
        updates[phoneKey] = customers;
      }

      if (!cancelled && Object.keys(updates).length) {
        setPhoneCustomerMap((previous) => ({
          ...previous,
          ...updates,
        }));
      }
    };

    loadMissingPhoneNames();

    return () => {
      cancelled = true;
    };
  }, [messages, phoneCustomerMap]);

  const filteredMessages = useMemo(() => {
    const q = normalizeSearchText(search);
    if (!q) return messages;

    return messages.filter((item) => {
      const blob = normalizeSearchText([
        item?.phone_number,
        item?.contact_name,
        item?.message_text,
        item?.state,
        item?.direction,
        item?.provider_message_id,
        item?.source,
      ].filter(Boolean).join(' '));

      return blob.includes(q);
    });
  }, [messages, search]);

  const unreadCount = useMemo(
    () => messages.filter((m) => m?.direction === 'inbound' && !m?.read_at).length,
    [messages]
  );

  const outboundCount = useMemo(
    () => messages.filter((m) => (
      m?.direction === 'outbound'
      && !['NotSentOffline', 'Failed', 'Error'].includes(String(m?.state || ''))
    )).length,
    [messages]
  );

  const notSentCount = useMemo(
    () => messages.filter((m) => (
      m?.direction === 'outbound'
      && ['NotSentOffline', 'Failed', 'Error'].includes(String(m?.state || ''))
    )).length,
    [messages]
  );

  const inboundCount = useMemo(
    () => messages.filter((m) => m?.direction === 'inbound').length,
    [messages]
  );

  const deviceStatus = useMemo(
    () => deviceLastSeenStatus(status?.device?.lastSeen || status?.device?.updatedAt || ''),
    [status]
  );

  const conversationMessages = useMemo(() => {
    const selectedPhone = normalizePhoneKey(selectedConversationSms?.phone_number);
    if (!selectedPhone) return [];

    return messages
      .filter((item) => normalizePhoneKey(item?.phone_number) === selectedPhone)
      .slice()
      .sort((a, b) => new Date(smsDateValue(a)).getTime() - new Date(smsDateValue(b)).getTime());
  }, [messages, selectedConversationSms]);

  const conversationCustomerNames = useMemo(() => {
    const names = conversationCustomers
      .map(customerDisplayName)
      .map((name) => String(name || '').trim())
      .filter(Boolean);

    return Array.from(new Set(names));
  }, [conversationCustomers]);

  const conversationTitle = conversationCustomerNames.length
    ? conversationCustomerNames.join(' / ')
    : 'Conversa SMS';

  useEffect(() => {
    if (!selectedConversationSms) return;

    const timer = window.setTimeout(() => {
      conversationEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 120);

    return () => window.clearTimeout(timer);
  }, [selectedConversationSms, conversationMessages.length]);

  const handleSyncStatus = async () => {
    setSyncingStatus(true);
    try {
      const res = await communicationsSmsAPI.syncStatus(100);
      const checked = res?.data?.checked ?? 0;
      const updated = res?.data?.updated ?? 0;
      const errors = Array.isArray(res?.data?.errors) ? res.data.errors.length : 0;

      if (errors > 0) {
        toast.warning(`Estados atualizados com avisos: ${updated}/${checked}. Erros: ${errors}`);
      } else {
        toast.success(`Estados atualizados: ${updated}/${checked}`);
      }

      await loadMessages();
    } catch (error) {
      console.error('Erro ao atualizar estados SMSGate:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao atualizar estados das SMS');
    } finally {
      setSyncingStatus(false);
    }
  };

  const handleSyncInbox = async () => {
    setSyncing(true);
    try {
      const res = await communicationsSmsAPI.syncInbox();

      if (res?.data?.inbox_supported === false) {
        toast.info(res?.data?.message || 'O SMSGate ainda não disponibiliza a leitura de SMS recebidas.');
        return;
      }

      const importedCount = res?.data?.saved ?? res?.data?.count ?? 0;
      toast.success(`Sincronização concluída: ${importedCount} SMS importadas`);
      await loadMessages();
    } catch (error) {
      console.error('Erro ao sincronizar inbox SMSGate:', error);
      toast.error(error?.response?.data?.detail || 'O SMSGate ainda não disponibiliza inbox neste endpoint');
    } finally {
      setSyncing(false);
    }
  };

  const selectedCustomerIds = useMemo(
    () => new Set(selectedCustomers.map((customer) => String(customer?.id || ''))),
    [selectedCustomers]
  );

  const selectedPhones = useMemo(
    () => selectedCustomers.map(customerPhone).filter(Boolean),
    [selectedCustomers]
  );

  const finalRecipients = useMemo(() => {
    const manual = parseRecipientPhones(sendForm.to);
    return Array.from(new Set([...manual, ...selectedPhones]));
  }, [sendForm.to, selectedPhones]);

  const resetSendModal = () => {
    setShowSendModal(false);
    setSendForm({ to: '', message: '', schedule_at: '' });
    setCustomerSearch('');
    setCustomerResults([]);
    setSelectedCustomers([]);
  };

  const searchCustomersForSms = async () => {
    const q = String(customerSearch || '').trim();
    if (q.length < 2) {
      toast.info('Escreva pelo menos 2 letras ou números para pesquisar clientes');
      return;
    }

    setSearchingCustomers(true);
    try {
      const res = await communicationsSmsAPI.searchCustomers(q, 80);
      const data = Array.isArray(res?.data) ? res.data : [];
      const filtered = data.filter((customer) => {
        const blob = normalizeSearchText([
          customerDisplayName(customer),
          customer?.nif,
          customer?.email,
          customerPhone(customer),
        ].filter(Boolean).join(' '));

        const qTokens = normalizeSearchText(q).split(/\s+/).filter(Boolean);
        return blob.includes(normalizeSearchText(q)) || qTokens.every((token) => blob.includes(token));
      });

      setCustomerResults(filtered);
      if (!filtered.length) {
        toast.info('Não encontrei clientes para essa pesquisa');
      }
    } catch (error) {
      console.error('Erro ao pesquisar clientes:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao pesquisar clientes');
    } finally {
      setSearchingCustomers(false);
    }
  };

  const addCustomerRecipient = (customer) => {
    const phone = customerPhone(customer);
    if (!phone) {
      toast.error('Este cliente não tem telemóvel registado');
      return;
    }

    setSelectedCustomers((previous) => {
      const id = String(customer?.id || '');
      if (id && previous.some((item) => String(item?.id || '') === id)) {
        return previous;
      }

      if (!id && previous.some((item) => customerPhone(item) === phone)) {
        return previous;
      }

      return [...previous, customer];
    });
  };

  const removeCustomerRecipient = (customer) => {
    setSelectedCustomers((previous) =>
      previous.filter((item) => {
        if (customer?.id && item?.id) return String(item.id) !== String(customer.id);
        return customerPhone(item) !== customerPhone(customer);
      })
    );
  };

  const openCustomerEditFromSms = (customer) => {
    if (!customer?.id) {
      toast.error('Não consegui identificar a ficha deste cliente');
      return;
    }

    const params = new URLSearchParams();
    params.set('sms_customer_id', String(customer.id));

    const label = customerDisplayName(customer) || customer?.nif || customer?.email || '';
    if (label) {
      params.set('sms_q', label);
    }

    window.location.href = `/customers?${params.toString()}`;
  };

  const handleSendSms = async () => {
    const message = String(sendForm.message || '').trim();
    const recipients = finalRecipients;

    if (!recipients.length) {
      toast.error('Indique pelo menos um telemóvel ou selecione clientes');
      return;
    }

    if (!message) {
      toast.error('Escreva a mensagem');
      return;
    }

    setSending(true);
    try {
      let sentCount = 0;
      let queuedCount = 0;
      const errors = [];

      for (const recipient of recipients) {
        try {
          const res = await communicationsSmsAPI.send({
            to: recipient,
            message,
            schedule_at: sendForm.schedule_at || null,
            source: recipients.length > 1 ? 'manual_frontend_multi' : 'manual_frontend',
          });

          if (res?.data?.queued_local_only) queuedCount += 1;
          else sentCount += 1;
        } catch (error) {
          console.error('Erro ao enviar SMS para destinatário:', recipient, error);
          errors.push({
            recipient,
            detail: error?.response?.data?.detail || 'Erro ao enviar',
          });
        }
      }

      if (sentCount > 0 && queuedCount === 0 && errors.length === 0) {
        toast.success(`${sentCount} SMS enviada${sentCount === 1 ? '' : 's'} para o SMSGate`);
      } else if (queuedCount > 0 && sentCount === 0 && errors.length === 0) {
        toast.warning(`${queuedCount} SMS guardada${queuedCount === 1 ? '' : 's'} como não enviada${queuedCount === 1 ? '' : 's'} para reenvio posterior`);
      } else {
        toast.warning(`Resultado: ${sentCount} enviada(s), ${queuedCount} não enviada(s), ${errors.length} erro(s)`);
      }

      if (errors.length) {
        console.warn('Erros no envio multi SMS:', errors);
      }

      resetSendModal();
      await loadMessages();
    } finally {
      setSending(false);
    }
  };

  const handleResendSms = async (smsId) => {
    if (!smsId) return;

    if (!window.confirm('Reenviar esta SMS agora pelo SMSGate?')) return;

    try {
      await communicationsSmsAPI.resend(smsId);
      toast.success('SMS reenviada para o SMSGate');
      await loadMessages();
    } catch (error) {
      console.error('Erro ao reenviar SMS:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao reenviar SMS');
      await loadMessages();
    }
  };

  const handleDeleteLocalSms = async (smsId) => {
    if (!smsId) return;

    if (!window.confirm('Apagar esta SMS local não enviada?')) return;

    try {
      await communicationsSmsAPI.deleteLocal(smsId);
      toast.success('SMS local apagada');
      await loadMessages();
    } catch (error) {
      console.error('Erro ao apagar SMS local:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao apagar SMS local');
    }
  };

  const handleMarkRead = async (smsId, read = true) => {
    if (!smsId) return;

    try {
      await communicationsSmsAPI.markRead(smsId, read);
      await loadMessages();
    } catch (error) {
      console.error('Erro ao atualizar leitura da SMS:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao atualizar estado de leitura');
    }
  };

  const findCustomersByPhone = async (phone, limit = 20) => {
    const phoneKey = normalizePhoneKey(phone);
    const phoneDigits = phoneKey.replace(/\D+/g, '');
    const nationalDigits = phoneNationalDigits(phone);

    if (!nationalDigits) return [];

    const queries = Array.from(new Set([nationalDigits, phoneDigits].filter(Boolean)));
    const combined = [];

    for (const q of queries) {
      try {
        const res = await communicationsSmsAPI.searchCustomers(q, limit);
        const data = Array.isArray(res?.data) ? res.data : [];
        combined.push(...data);
      } catch (innerError) {
        console.warn('Pesquisa parcial de cliente por telefone falhou:', q, innerError);
      }
    }

    const unique = [];
    const seen = new Set();

    for (const customer of combined) {
      const key = String(customer?.id || customerPhone(customer) || customerDisplayName(customer));
      if (!key || seen.has(key)) continue;
      seen.add(key);
      unique.push(customer);
    }

    const exactMatches = unique.filter((customer) => {
      const customerNational = phoneNationalDigits(customerPhone(customer));
      return customerNational && customerNational === nationalDigits;
    });

    return exactMatches.length ? exactMatches : unique.slice(0, 3);
  };

  const customerNamesForPhone = (phone) => {
    const key = phoneNationalDigits(phone);
    const customers = key ? (phoneCustomerMap[key] || []) : [];
    const names = customers
      .map(customerDisplayName)
      .map((name) => String(name || '').trim())
      .filter(Boolean);

    return Array.from(new Set(names));
  };

  const customerLabelForPhone = (phone) => {
    const names = customerNamesForPhone(phone);
    return names.length ? names.join(' / ') : '';
  };

  const groupedConversations = useMemo(() => {
    const groups = new Map();

    for (const item of filteredMessages) {
      const key = phoneNationalDigits(item?.phone_number) || normalizePhoneKey(item?.phone_number) || item?.id;
      if (!key) continue;

      const dateValue = smsDateValue(item);
      const dateMs = new Date(dateValue).getTime() || 0;

      const existing = groups.get(key) || {
        key,
        phone_number: item?.phone_number || '',
        messages: [],
        total: 0,
        unread: 0,
        inbound: 0,
        outbound: 0,
        latestDateMs: 0,
        latestMessage: null,
        latestUnreadInbound: null,
      };

      existing.messages.push(item);
      existing.total += 1;

      if (item?.direction === 'inbound') {
        existing.inbound += 1;
        if (!item?.read_at) {
          existing.unread += 1;
          const unreadDateMs = new Date(smsDateValue(item)).getTime() || 0;
          const currentUnreadDateMs = existing.latestUnreadInbound
            ? (new Date(smsDateValue(existing.latestUnreadInbound)).getTime() || 0)
            : 0;

          if (!existing.latestUnreadInbound || unreadDateMs >= currentUnreadDateMs) {
            existing.latestUnreadInbound = item;
          }
        }
      }

      if (item?.direction === 'outbound') {
        existing.outbound += 1;
      }

      if (!existing.latestMessage || dateMs >= existing.latestDateMs) {
        existing.latestDateMs = dateMs;
        existing.latestMessage = item;
        existing.phone_number = item?.phone_number || existing.phone_number;
      }

      groups.set(key, existing);
    }

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        openMessage: group.latestUnreadInbound || group.latestMessage || group.messages[0],
      }))
      .sort((a, b) => (b.latestDateMs || 0) - (a.latestDateMs || 0));
  }, [filteredMessages]);

  const loadConversationCustomers = async (phone) => {
    const nationalDigits = phoneNationalDigits(phone);

    if (!nationalDigits) {
      setConversationCustomers([]);
      return;
    }

    setConversationCustomerLoading(true);
    try {
      const customers = await findCustomersByPhone(phone, 20);

      setPhoneCustomerMap((previous) => ({
        ...previous,
        [nationalDigits]: customers,
      }));

      setConversationCustomers(customers);
    } catch (error) {
      console.error('Erro ao procurar cliente da conversa SMS:', error);
      setConversationCustomers([]);
    } finally {
      setConversationCustomerLoading(false);
    }
  };

  const handleOpenConversation = async (item) => {
    if (!item) return;

    setSelectedConversationSms(item);
    setConversationReply('');
    setConversationCustomers([]);
    loadConversationCustomers(item?.phone_number);

    if (item?.direction === 'inbound' && !item?.read_at && item?.id) {
      try {
        await communicationsSmsAPI.markRead(item.id, true);
        setSelectedConversationSms((previous) =>
          previous?.id === item.id
            ? { ...previous, read_at: new Date().toISOString() }
            : previous
        );
        await loadMessages();
      } catch (error) {
        console.error('Erro ao marcar SMS como lida ao abrir conversa:', error);
      }
    }
  };

  const handleCloseConversation = () => {
    setSelectedConversationSms(null);
    setConversationReply('');
    setConversationCustomers([]);
    setConversationCustomerLoading(false);
  };

  const handleSendConversationReply = async () => {
    const to = String(selectedConversationSms?.phone_number || '').trim();
    const message = String(conversationReply || '').trim();

    if (!to) {
      toast.error('Esta conversa não tem número de telemóvel válido');
      return;
    }

    if (!message) {
      toast.error('Escreva a resposta');
      return;
    }

    setConversationSending(true);
    try {
      const res = await communicationsSmsAPI.send({
        to,
        message,
        schedule_at: null,
        source: 'conversation_modal',
      });

      if (res?.data?.queued_local_only) {
        toast.warning('Resposta guardada como não enviada para reenvio posterior');
      } else {
        toast.success('Resposta enviada para o SMSGate');
      }

      setConversationReply('');
      await loadMessages();

      window.setTimeout(async () => {
        try {
          await communicationsSmsAPI.syncStatus(50);
          await loadMessages();
        } catch (syncError) {
          console.warn('Não foi possível atualizar estados SMS após resposta:', syncError);
        }
      }, 2500);
    } catch (error) {
      console.error('Erro ao responder por SMS:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao enviar resposta');
      await loadMessages();
    } finally {
      setConversationSending(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <MessageCircle className="w-7 h-7 text-green-400" />
            Comunicações SMS
          </h1>
          <p className="text-slate-400 mt-1">
            Envio manual, histórico local e ligação ao SMSGate Cloud.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => {
              loadStatus();
              loadMessages();
            }}
            disabled={loading || statusLoading}
          >
            {(loading || statusLoading) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
            Atualizar
          </Button>

          <Button
            variant="outline"
            onClick={handleSyncStatus}
            disabled={syncingStatus}
            title="Consulta o SMSGate e atualiza estados Pending/Processed/Delivered/Failed. Não envia SMS."
          >
            {syncingStatus ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
            Atualizar estados
          </Button>

          <Button
            variant="outline"
            onClick={handleSyncInbox}
            disabled={syncing}
            title="As recebidas entram automaticamente por webhook. Este botão tenta apenas uma verificação manual adicional."
          >
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Inbox className="w-4 h-4 mr-2" />}
            Verificar recebidas
          </Button>

          <Button
            onClick={() => setShowSendModal(true)}
            className="bg-green-600 hover:bg-green-500 text-white"
          >
            <Send className="w-4 h-4 mr-2" />
            Enviar SMS
          </Button>
        </div>
      </div>
      <div className="rounded-xl border border-green-800 bg-green-950/25 p-4 text-sm text-green-100 flex items-start gap-3">
        <CheckCheck className="w-5 h-5 text-green-300 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold text-green-100">Leitura de SMS recebidas ativa</div>
          <div className="text-green-200/80 mt-1">
            As SMS recebidas via webhook público do SMSGate já estão a ser registadas no histórico local. Clique numa SMS para abrir a conversa e responder diretamente.
          </div>
        </div>
      </div>


      {deviceStatus?.stale && (
        <div className="rounded-xl border border-red-800 bg-red-950/30 p-4 text-sm text-red-100 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-300 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-red-100">Telemóvel SMSGate possivelmente offline</div>
            <div className="text-red-200/80 mt-1">
              O SMSGate identifica o dispositivo como {deviceStatus.label.toLowerCase()} ({deviceStatus.detail}). Enquanto o telemóvel não voltar a comunicar com a cloud, as SMS podem ficar apenas em fila como “Pendente”.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
              <Smartphone className="w-4 h-4" />
              SMSGate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-lg font-bold ${deviceStatus?.stale ? 'text-red-300' : 'text-white'}`}>
              {status?.ok ? deviceStatus.label : 'Por confirmar'}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {status?.device?.name || status?.device_id || 'Sem dispositivo carregado'}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Último contacto: {status?.device?.lastSeen ? formatDateTime(status.device.lastSeen) : '-'}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
              <Send className="w-4 h-4" />
              Enviadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{outboundCount}</div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Não enviadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-300">{notSentCount}</div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
              <Inbox className="w-4 h-4" />
              Recebidas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{inboundCount}</div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Por ler
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{unreadCount}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Pesquisar por número, texto, estado..."
                className="pl-9 bg-slate-950 border-slate-700 text-white"
              />
            </div>

            <select
              value={filterDirection}
              onChange={(e) => setFilterDirection(e.target.value)}
              className="bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-2 text-sm"
            >
              <option value="all">Todas</option>
              <option value="outbound">Enviadas</option>
              <option value="inbound">Recebidas</option>
            </select>

            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={filterUnread}
                onChange={(e) => setFilterUnread(e.target.checked)}
              />
              Só por ler
            </label>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Conversas SMS</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
              A carregar comunicações...
            </div>
          ) : groupedConversations.length === 0 ? (
            <div className="py-12 text-center text-slate-500">
              Ainda não existem conversas SMS para mostrar.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-300">Conversa</TableHead>
                    <TableHead className="text-slate-300">Última mensagem</TableHead>
                    <TableHead className="text-slate-300">Estado</TableHead>
                    <TableHead className="text-slate-300">Resumo</TableHead>
                    <TableHead className="text-slate-300">Última atividade</TableHead>
                    <TableHead className="text-slate-300 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedConversations.map((group) => {
                    const item = group.latestMessage || group.messages[0] || {};
                    const openItem = group.openMessage || item;
                    const dateValue = smsDateValue(item);
                    const label = customerLabelForPhone(group.phone_number || item?.phone_number);
                    const lastIsInbound = item?.direction === 'inbound';
                    const hasFailedOutbound = group.messages.some((sms) => (
                      sms?.direction === 'outbound'
                      && ['NotSentOffline', 'Failed', 'Error'].includes(String(sms?.state || ''))
                    ));

                    return (
                      <TableRow
                        key={group.key}
                        className={`border-slate-800 cursor-pointer hover:bg-slate-800/45 ${group.unread > 0 ? 'bg-orange-950/10' : ''}`}
                        onClick={() => handleOpenConversation(openItem)}
                        title="Clique para abrir a conversa SMS"
                      >
                        <TableCell className="text-slate-200 min-w-[260px]">
                          <div className="flex items-start gap-2">
                            <Phone className="w-4 h-4 text-slate-500 mt-1 shrink-0" />
                            <div className="min-w-0">
                              <div className="font-medium text-slate-100 truncate max-w-[300px]">
                                {label || group.phone_number || item?.phone_number || '-'}
                              </div>
                              <div className={`${label ? 'text-xs text-slate-500 mt-0.5' : 'text-sm text-slate-400 mt-0.5'}`}>
                                {group.phone_number || item?.phone_number || '-'}
                              </div>
                              {group.unread > 0 && (
                                <Badge className="mt-2 bg-orange-900/40 text-orange-300 border-orange-800">
                                  {group.unread} por ler
                                </Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>

                        <TableCell className="text-slate-300 max-w-xl">
                          <div className="flex items-start gap-2">
                            {lastIsInbound ? (
                              <Inbox className={`w-4 h-4 mt-0.5 shrink-0 ${communicationTypeIconClass(item)}`} />
                            ) : (
                              <Send className={`w-4 h-4 mt-0.5 shrink-0 ${communicationTypeIconClass(item)}`} />
                            )}
                            <div className="min-w-0">
                              <div className="text-xs text-slate-500 mb-1">
                                {lastIsInbound ? 'Última recebida' : 'Última enviada'}
                              </div>
                              <div className="line-clamp-2 whitespace-pre-wrap">
                                {item?.message_text || '-'}
                              </div>
                            </div>
                          </div>
                        </TableCell>

                        <TableCell>
                          <Badge className={stateBadgeClass(item?.state)}>
                            {stateLabel(item?.state)}
                          </Badge>
                        </TableCell>

                        <TableCell className="text-slate-400">
                          <div className="text-sm">
                            {group.total} mensagem{group.total === 1 ? '' : 's'}
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            {group.inbound} recebida{group.inbound === 1 ? '' : 's'} · {group.outbound} enviada{group.outbound === 1 ? '' : 's'}
                          </div>
                        </TableCell>

                        <TableCell className="text-slate-400">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-500" />
                            {formatDateTime(dateValue)}
                          </div>
                        </TableCell>

                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {hasFailedOutbound && (
                              <Badge className="bg-red-900/40 text-red-300 border-red-800">
                                Falhas
                              </Badge>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenConversation(openItem);
                              }}
                            >
                              <MessageCircle className="w-4 h-4 mr-1" />
                              Abrir
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>


      <Dialog
        open={!!selectedConversationSms}
        onOpenChange={(open) => {
          if (!open) handleCloseConversation();
        }}
      >
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-300" />
              Conversa SMS
            </DialogTitle>
          </DialogHeader>

          <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="text-sm text-slate-400">
                  {conversationCustomerLoading ? 'A procurar cliente...' : conversationCustomerNames.length ? 'Cliente' : 'Número'}
                </div>
                <div className="text-lg font-semibold text-slate-100 flex items-center gap-2 min-w-0">
                  <Phone className="w-4 h-4 text-slate-500 shrink-0" />
                  <span className="truncate">{conversationTitle}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {selectedConversationSms?.phone_number || '-'}
                </div>

                {conversationCustomers.length > 1 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {conversationCustomers.slice(0, 4).map((customer) => (
                      <span
                        key={customer?.id || customerPhone(customer) || customerDisplayName(customer)}
                        className="rounded-full border border-slate-700 bg-slate-900 px-2 py-1 text-[11px] text-slate-300"
                      >
                        {customerDisplayName(customer)}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="text-xs text-slate-500 shrink-0">
                {conversationMessages.length} mensagem{conversationMessages.length === 1 ? '' : 's'} nesta conversa
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-[320px] max-h-[52vh] overflow-y-auto rounded-lg border border-slate-800 bg-slate-950/50 p-4 space-y-3">
            {conversationMessages.length === 0 ? (
              <div className="py-10 text-center text-slate-500">
                Ainda não há histórico para este número.
              </div>
            ) : (
              conversationMessages.map((sms) => {
                const isInbound = sms?.direction === 'inbound';
                const dateValue = smsDateValue(sms);

                return (
                  <div
                    key={sms.id}
                    className={`flex ${isInbound ? 'justify-start' : 'justify-end'}`}
                  >
                    <div className={`max-w-[82%] rounded-2xl px-4 py-3 border ${
                      isInbound
                        ? 'bg-slate-800 border-slate-700 text-slate-100 rounded-bl-sm'
                        : 'bg-green-900/45 border-green-800 text-green-50 rounded-br-sm'
                    }`}>
                      <div className="whitespace-pre-wrap text-sm leading-relaxed">
                        {sms?.message_text || '-'}
                      </div>
                      <div className={`mt-2 flex flex-wrap items-center gap-2 text-[11px] ${
                        isInbound ? 'text-slate-400' : 'text-green-200/75'
                      }`}>
                        <span>{isInbound ? 'Recebida' : 'Enviada'}</span>
                        <span>·</span>
                        <span>{formatDateTime(dateValue)}</span>
                        {!isInbound && (
                          <>
                            <span>·</span>
                            <span>{stateLabel(sms?.state)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={conversationEndRef} />
          </div>

          <div className="space-y-2 border-t border-slate-800 pt-4">
            <label className="text-sm text-slate-300 block">Responder por SMS</label>
            <Textarea
              value={conversationReply}
              onChange={(e) => setConversationReply(e.target.value)}
              rows={4}
              maxLength={480}
              placeholder="Escreva a resposta..."
              className="bg-slate-950 border-slate-700 text-white resize-none"
            />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-slate-500">
                {conversationReply.length}/480 caracteres
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={handleCloseConversation}
                  disabled={conversationSending}
                >
                  Fechar
                </Button>
                <Button
                  onClick={handleSendConversationReply}
                  disabled={conversationSending}
                  className="bg-green-600 hover:bg-green-500 text-white"
                >
                  {conversationSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Enviar resposta
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showSendModal} onOpenChange={setShowSendModal}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Enviar SMS manual</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Telemóveis</label>
              <Input
                value={sendForm.to}
                onChange={(e) => setSendForm((prev) => ({ ...prev, to: e.target.value }))}
                placeholder="926550649, 962244345, 911222444"
                className="bg-slate-950 border-slate-700 text-white"
              />
              <div className="text-xs text-slate-500 mt-1">
                Pode escrever vários números separados por vírgula, com ou sem espaços.
              </div>
            </div>

            <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3 space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="flex-1">
                  <label className="text-sm text-slate-300 mb-1 block">Pesquisar clientes</label>
                  <Input
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        searchCustomersForSms();
                      }
                    }}
                    placeholder="Nome, NIF, email ou telemóvel"
                    className="bg-slate-950 border-slate-700 text-white"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={searchCustomersForSms}
                    disabled={searchingCustomers}
                    className="w-full sm:w-auto"
                  >
                    {searchingCustomers ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Pesquisar
                  </Button>
                </div>
              </div>

              {selectedCustomers.length > 0 && (
                <div>
                  <div className="text-xs text-slate-400 mb-2">Clientes selecionados</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedCustomers.map((customer) => (
                      <button
                        key={customer?.id || customerPhone(customer)}
                        type="button"
                        onClick={() => removeCustomerRecipient(customer)}
                        className="rounded-full border border-green-800 bg-green-950/40 px-3 py-1 text-xs text-green-200 hover:bg-green-900/50"
                        title="Clique para remover"
                      >
                        {customerDisplayName(customer)} · {customerPhone(customer)} ×
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {customerResults.length > 0 && (
                <div className="max-h-44 overflow-y-auto border border-slate-800 rounded-lg divide-y divide-slate-800">
                  {customerResults.map((customer) => {
                    const id = String(customer?.id || '');
                    const selected = id ? selectedCustomerIds.has(id) : selectedPhones.includes(customerPhone(customer));

                    return (
                      <button
                        key={customer?.id || customerPhone(customer)}
                        type="button"
                        onClick={() => {
                          if (!customerPhone(customer)) {
                            openCustomerEditFromSms(customer);
                            return;
                          }
                          selected ? removeCustomerRecipient(customer) : addCustomerRecipient(customer);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-800/70 ${selected ? 'bg-green-950/30' : 'bg-slate-950/40'} ${!customerPhone(customer) ? 'opacity-70' : ''}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="text-slate-100 font-medium">{customerDisplayName(customer)}</div>
                            <div className="text-xs text-slate-500">
                              {customerPhone(customer) || 'Sem telemóvel registado'}{customer?.nif ? ` · NIF ${customer.nif}` : ''}
                            </div>
                          </div>
                          <div className={`text-xs ${customerPhone(customer) ? 'text-green-300' : 'text-orange-300'}`}>
                            {!customerPhone(customer) ? 'Editar ficha' : selected ? 'Selecionado' : 'Adicionar'}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="text-xs text-slate-400">
                Destinatários finais: {finalRecipients.length || 0}
              </div>
            </div>

            <div>
              <label className="text-sm text-slate-300 mb-1 block">Mensagem</label>
              <textarea
                value={sendForm.message}
                onChange={(e) => setSendForm((prev) => ({ ...prev, message: e.target.value }))}
                rows={6}
                maxLength={480}
                placeholder="Escreva a mensagem..."
                className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-green-600"
              />
              <div className="text-xs text-slate-500 mt-1">
                {sendForm.message.length}/480 caracteres
              </div>
            </div>

            <div>
              <label className="text-sm text-slate-300 mb-1 block">Agendar para, opcional</label>
              <Input
                type="datetime-local"
                value={sendForm.schedule_at}
                onChange={(e) => setSendForm((prev) => ({ ...prev, schedule_at: e.target.value }))}
                className="bg-slate-950 border-slate-700 text-white"
              />
            </div>

            <div className="rounded-lg border border-yellow-800 bg-yellow-950/30 p-3 text-sm text-yellow-200">
              Atenção: ao confirmar, a mesma SMS será processada para todos os destinatários indicados. Se o telemóvel SMSGate estiver offline, ficará guardada como não enviada para reenvio posterior.
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendModal(false)} disabled={sending}>
              Cancelar
            </Button>
            <Button onClick={handleSendSms} disabled={sending} className="bg-green-600 hover:bg-green-500 text-white">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar SMS{finalRecipients.length > 1 ? ` (${finalRecipients.length})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
